import { useEffect, useRef, useState } from 'react';
import { useCanvasHandlers } from './useCanvasHandlers';
import { useCommentZone } from './useCommentZone';
import { commentImageData, pageImageData, Point } from './Preview.types';
import { IComment } from '../Comments/Comments.types';
import { getTargetPageImage } from './previewUtils';
import { fabric } from 'fabric';

interface UseAddingCommentZoneActionProps {
  canvas: fabric.Canvas;
  getCanvasPoint: (clientX: number, clientY: number) => Point;
  onFinishAction?: (comment: IComment) => void;
  pageImages: pageImageData[];
  setCommentsToDisplay: (value: React.SetStateAction<IComment[] | undefined>) => void;
}

const SELECTOR_SIZE = 24;
const FRAME_COLOR = '#6284fb';

export const useAddingCommentZoneAction = ({
  canvas,
  getCanvasPoint,
  onFinishAction,
  pageImages,
  setCommentsToDisplay,
}: UseAddingCommentZoneActionProps) => {
  const [commentForAddingZoneAction, setCommentForAddingZoneAction] = useState<IComment | undefined>(undefined);
  const helperCanvasItems = useRef<fabric.Object[]>([]);
  const startPoint = useRef<[number, number] | undefined>(undefined);

  const clearHelperItems = () => {
    helperCanvasItems.current.forEach((item) => canvas.remove(item));
    helperCanvasItems.current = [];
    startPoint.current = undefined;
    setCommentForAddingZoneAction(undefined);
  };

  useEffect(() => {
    return () => clearHelperItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseUp = (x1: number, y1: number, x2?: number, y2?: number) => {
    const targetImageData: pageImageData | undefined = getTargetPageImage(pageImages, x1, y1);
    if (
      !commentForAddingZoneAction ||
      !targetImageData ||
      !targetImageData.image ||
      targetImageData.image.left === undefined ||
      targetImageData.image.top === undefined
    ) {
      clearHelperItems();
      return;
    }

    const upgradedComment = {
      ...commentForAddingZoneAction,
      x1: x1,
      y1: y1,
      x2: x2 ?? null,
      y2: y2 ?? null,
      pageNumber: targetImageData.pageNumber,
    };

    if (x2 && y2) {
      setCommentsToDisplay((prevState) => {
        return prevState?.map((item) => (item.commentId === upgradedComment.commentId ? upgradedComment : item));
      });
    }

    onFinishAction?.(upgradedComment);
    clearHelperItems();
  };

  const startAddingZone = (commentData: commentImageData) => {
    const { comment, image } = commentData;
    setCommentForAddingZoneAction(comment);
    canvas.discardActiveObject();

    const iconFrame = new fabric.Rect({
      left: image.left || 0,
      top: image.top || 0,
      width: image.width || 0,
      height: -(image.height || 0),
      scaleX: image.scaleX,
      scaleY: image.scaleY,
      fill: 'transparent',
      selectable: false,
      stroke: FRAME_COLOR,
      strokeWidth: 1,
    });

    startPoint.current = [image.left || 0, image.top || 0];

    const leftBottomPointer = new fabric.Rect({
      left: startPoint.current[0] - SELECTOR_SIZE / 2,
      top: startPoint.current[1] - SELECTOR_SIZE / 2,
      width: SELECTOR_SIZE,
      height: SELECTOR_SIZE,
      fill: 'transparent',
      selectable: false,
      stroke: FRAME_COLOR,
      strokeWidth: 1,
    });

    helperCanvasItems.current.push(iconFrame);
    helperCanvasItems.current.push(leftBottomPointer);
    canvas.add(iconFrame);
    canvas.add(leftBottomPointer);
    canvas.renderAll();
  };

  const { handleCanvasMouseDown, handleCanvasMouseMove, handleCanvasMouseUp } = useCommentZone({
    canvas,
    enabled: !!commentForAddingZoneAction,
    getCanvasPoint,
    forcedStartPoint: startPoint.current ? [startPoint.current[0], startPoint.current[1]] : undefined,
    onFinishDrawingZone: handleMouseUp,
  });

  useCanvasHandlers({
    canvas,
    enabled: !!commentForAddingZoneAction,
    onMouseDown: handleCanvasMouseDown,
    onMouseMove: handleCanvasMouseMove,
    onMouseUp: handleCanvasMouseUp,
  });

  return { commentForAddingZoneAction, startAddingZone };
};
